import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Search,
  BookOpen,
  Folder,
  FileText,
  Eye,
  EyeOff,
  Users,
  User,
  Building2,
  Edit,
  Trash2,
  Clock,
  ChevronRight,
  Tag
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const KnowledgeBasePage = () => {
  const { user, isAgency, isTeamMember, isClient } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewArticle, setShowNewArticle] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  
  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    category_id: '',
    target_audience: 'all', // 'all', 'creatives', 'clients', 'internal'
    tags: '',
    is_published: true
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    icon: 'folder'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        api.get('/knowledge/articles'),
        api.get('/knowledge/categories')
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
    } finally {
      setLoading(false);
    }
  };

  const createArticle = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newArticle,
        tags: newArticle.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      await api.post('/knowledge/articles', payload);
      toast.success('Article créé avec succès !');
      setShowNewArticle(false);
      setNewArticle({
        title: '',
        content: '',
        category_id: '',
        target_audience: 'all',
        tags: '',
        is_published: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const updateArticle = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editingArticle,
        tags: typeof editingArticle.tags === 'string' 
          ? editingArticle.tags.split(',').map(t => t.trim()).filter(t => t)
          : editingArticle.tags
      };
      await api.put(`/knowledge/articles/${editingArticle.id}`, payload);
      toast.success('Article mis à jour !');
      setEditingArticle(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/knowledge/articles/${articleId}`);
      toast.success('Article supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/knowledge/categories', newCategory);
      toast.success('Catégorie créée !');
      setShowNewCategory(false);
      setNewCategory({ name: '', description: '', icon: 'folder' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const getTargetAudienceInfo = (audience) => {
    const info = {
      all: { label: 'Tous', icon: Users, color: 'bg-blue-100 text-blue-700' },
      creatives: { label: 'Créatifs', icon: User, color: 'bg-purple-100 text-purple-700' },
      clients: { label: 'Clients', icon: Building2, color: 'bg-green-100 text-green-700' },
      internal: { label: 'Interne', icon: EyeOff, color: 'bg-gray-100 text-gray-700' }
    };
    return info[audience] || info.all;
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group articles by category
  const articlesByCategory = filteredArticles.reduce((acc, article) => {
    const catId = article.category_id || 'uncategorized';
    if (!acc[catId]) acc[catId] = [];
    acc[catId].push(article);
    return acc;
  }, {});

  const getCategoryName = (catId) => {
    if (catId === 'uncategorized') return 'Non catégorisé';
    const cat = categories.find(c => c.id === catId);
    return cat?.name || 'Non catégorisé';
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d]" data-testid="knowledge-title">
              Base de connaissances
            </h1>
            <p className="text-[#64748b] mt-1">
              {filteredArticles.length} article(s)
            </p>
          </div>
          
          {isAgency && (
            <div className="flex gap-2">
              <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Folder className="w-4 h-4 mr-2" />
                    Nouvelle catégorie
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Créer une catégorie</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nom de la catégorie</Label>
                      <Input
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        placeholder="Ex: Guides utilisateurs"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                        placeholder="Description de la catégorie..."
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowNewCategory(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="btn-primary">
                        Créer
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showNewArticle} onOpenChange={setShowNewArticle}>
                <DialogTrigger asChild>
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel article
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Créer un article</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createArticle} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        value={newArticle.title}
                        onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                        placeholder="Titre de l'article"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Catégorie</Label>
                        <Select 
                          value={newArticle.category_id} 
                          onValueChange={(v) => setNewArticle({ ...newArticle, category_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner une catégorie" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Visibilité</Label>
                        <Select 
                          value={newArticle.target_audience} 
                          onValueChange={(v) => setNewArticle({ ...newArticle, target_audience: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Tout le monde
                              </div>
                            </SelectItem>
                            <SelectItem value="creatives">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Créatifs uniquement
                              </div>
                            </SelectItem>
                            <SelectItem value="clients">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                Clients uniquement
                              </div>
                            </SelectItem>
                            <SelectItem value="internal">
                              <div className="flex items-center gap-2">
                                <EyeOff className="w-4 h-4" />
                                Interne (agence)
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Contenu</Label>
                      <Textarea
                        value={newArticle.content}
                        onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                        placeholder="Contenu de l'article (supporte Markdown)..."
                        className="min-h-[200px]"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Tags (séparés par des virgules)</Label>
                      <Input
                        value={newArticle.tags}
                        onChange={(e) => setNewArticle({ ...newArticle, tags: e.target.value })}
                        placeholder="guide, tutoriel, débutant"
                      />
                    </div>
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowNewArticle(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="btn-primary">
                        Publier
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher un article..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="input-default w-full md:w-[200px]">
                  <Folder className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : selectedArticle ? (
          // Article Detail View
          <Card className="card">
            <CardContent className="p-6">
              <Button 
                variant="ghost" 
                className="mb-4"
                onClick={() => setSelectedArticle(null)}
              >
                ← Retour à la liste
              </Button>
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-[#1a365d] mb-2">
                    {selectedArticle.title}
                  </h2>
                  <div className="flex items-center gap-3 text-sm text-[#64748b]">
                    <Badge className={getTargetAudienceInfo(selectedArticle.target_audience).color}>
                      {getTargetAudienceInfo(selectedArticle.target_audience).label}
                    </Badge>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(selectedArticle.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                
                {isAgency && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setEditingArticle({
                          ...selectedArticle,
                          tags: selectedArticle.tags?.join(', ') || ''
                        });
                        setSelectedArticle(null);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                  </div>
                )}
              </div>
              
              {selectedArticle.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedArticle.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="prose prose-slate max-w-none">
                {selectedArticle.content.split('\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-4 text-[#334155]">
                    {paragraph}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : filteredArticles.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold text-[#1a365d] mb-2">Aucun article trouvé</h3>
              <p className="text-[#64748b]">
                {searchQuery || selectedCategory !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Créez votre premier article pour commencer'}
              </p>
            </CardContent>
          </Card>
        ) : (
          // Articles List grouped by category
          <div className="space-y-6">
            {Object.entries(articlesByCategory).map(([catId, catArticles]) => (
              <Card key={catId} className="card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Folder className="w-5 h-5 text-[#00d4aa]" />
                    {getCategoryName(catId)}
                    <Badge variant="secondary" className="ml-2">
                      {catArticles.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="divide-y divide-[#e2e8f0]">
                    {catArticles.map((article) => {
                      const audienceInfo = getTargetAudienceInfo(article.target_audience);
                      const AudienceIcon = audienceInfo.icon;
                      
                      return (
                        <div
                          key={article.id}
                          className="py-4 flex items-center justify-between hover:bg-[#f8fafc] -mx-6 px-6 cursor-pointer group"
                          onClick={() => setSelectedArticle(article)}
                        >
                          <div className="flex items-start gap-4 flex-1">
                            <div className="w-10 h-10 bg-[#f1f5f9] rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#64748b]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-[#1a365d] group-hover:text-[#00d4aa] transition-colors">
                                {article.title}
                              </h4>
                              <p className="text-sm text-[#64748b] line-clamp-1 mt-1">
                                {article.content?.substring(0, 150)}...
                              </p>
                              <div className="flex items-center gap-3 mt-2">
                                <Badge className={cn("text-xs", audienceInfo.color)}>
                                  <AudienceIcon className="w-3 h-3 mr-1" />
                                  {audienceInfo.label}
                                </Badge>
                                <span className="text-xs text-[#94a3b8]">
                                  {new Date(article.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {isAgency && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingArticle({
                                      ...article,
                                      tags: article.tags?.join(', ') || ''
                                    });
                                  }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteArticle(article.id);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <ChevronRight className="w-5 h-5 text-[#94a3b8] group-hover:text-[#00d4aa]" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Article Dialog */}
        <Dialog open={!!editingArticle} onOpenChange={(open) => !open && setEditingArticle(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier l'article</DialogTitle>
            </DialogHeader>
            {editingArticle && (
              <form onSubmit={updateArticle} className="space-y-4">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input
                    value={editingArticle.title}
                    onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select 
                      value={editingArticle.category_id || ''} 
                      onValueChange={(v) => setEditingArticle({ ...editingArticle, category_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Visibilité</Label>
                    <Select 
                      value={editingArticle.target_audience} 
                      onValueChange={(v) => setEditingArticle({ ...editingArticle, target_audience: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tout le monde</SelectItem>
                        <SelectItem value="creatives">Créatifs uniquement</SelectItem>
                        <SelectItem value="clients">Clients uniquement</SelectItem>
                        <SelectItem value="internal">Interne (agence)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Contenu</Label>
                  <Textarea
                    value={editingArticle.content}
                    onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })}
                    className="min-h-[200px]"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tags (séparés par des virgules)</Label>
                  <Input
                    value={editingArticle.tags}
                    onChange={(e) => setEditingArticle({ ...editingArticle, tags: e.target.value })}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingArticle(null)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Enregistrer
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default KnowledgeBasePage;
